/**
 * Single JWT payload decoder for the whole app (no signature verification — client-side
 * claims access only). Handles base64url, missing padding and UTF-8 payloads, all of
 * which the previous per-file `atob(...)` copies got subtly wrong.
 *
 * Returns `null` when the token is not a decodable JWT. Callers must treat `null` as
 * "could not read the token", which is NOT the same as a token with empty claims.
 */
export function decodeJwtPayload(token: string | null | undefined): any | null {
  if (!token || typeof token !== 'string') return null;

  const part = token.split('.')[1];
  if (!part) return null;

  try {
    const base64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const bytes = Uint8Array.from(atob(padded), (c) => c.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes));
  } catch {
    return null;
  }
}

/**
 * Reads a claim that PlatformAuth emits as a list of strings.
 *
 * .NET's JWT writer collapses a SINGLE-element collection to a bare string, so
 * `["Owner"]` is serialised as `"app_roles":"Owner"` while a two-element list stays an
 * array. An `Array.isArray()` check alone therefore silently drops every single-valued
 * list claim — which is how Owner (always emitted alone) never reached the client.
 */
export function claimToStringArray(claim: unknown): string[] {
  if (Array.isArray(claim)) return claim.filter((v): v is string => typeof v === 'string' && v.length > 0);
  if (typeof claim === 'string' && claim.length > 0) return [claim];
  return [];
}
